import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ProjectsExplorer.css";
import ProjectsData from "../../shared/opensource/projects.json";
import { gsap, ScrollTrigger, prefersReducedMotion } from "../../utils/gsap";

const fmt = (iso) => {
  const m = /^(\d{4})-(\d{2})/.exec(iso || "");
  if (!m) return "";
  return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString("en", {
    month: "short",
    year: "numeric",
  });
};

const uniqueLangs = (p) =>
  p.languages.filter((l, i, a) => a.findIndex((x) => x.name === l.name) === i);

// Searchable, filterable, sortable project showcase.
export default function ProjectsExplorer() {
  const root = useRef(null);
  const [tag, setTag] = useState("All");
  const [sort, setSort] = useState("newest");
  const [query, setQuery] = useState("");

  const all = useMemo(() => ProjectsData.data, []);
  const tags = useMemo(() => {
    const counts = new Map();
    all.forEach((p) =>
      uniqueLangs(p).forEach((l) => counts.set(l.name, (counts.get(l.name) || 0) + 1))
    );
    return [
      { name: "All", count: all.length },
      ...[...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([name, count]) => ({ name, count })),
    ];
  }, [all]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all
      .filter((p) => tag === "All" || p.languages.some((l) => l.name === tag))
      .filter(
        (p) =>
          !q ||
          `${p.name} ${p.description} ${p.languages.map((l) => l.name).join(" ")}`
            .toLowerCase()
            .includes(q)
      )
      .slice()
      .sort((a, b) =>
        sort === "newest"
          ? b.createdAt.localeCompare(a.createdAt)
          : a.createdAt.localeCompare(b.createdAt)
      );
  }, [all, tag, sort, query]);

  // cards rise in as they enter the viewport; re-runs when the list changes
  const key = shown.map((p) => p.name).join("|");
  useEffect(() => {
    const el = root.current;
    if (!el || prefersReducedMotion()) return undefined;
    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray(".proj-card");
      gsap.set(cards, { autoAlpha: 0, y: 36 });
      ScrollTrigger.batch(cards, {
        start: "top 92%",
        once: true,
        onEnter: (batch) =>
          gsap.to(batch, {
            autoAlpha: 1,
            y: 0,
            duration: 0.7,
            stagger: 0.09,
            ease: "power3.out",
          }),
      });
    }, el);
    return () => ctx.revert();
  }, [key]);

  return (
    <div className="proj" ref={root}>
      <div className="proj-controls">
        <label className="proj-search">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
          <input
            type="search"
            placeholder="Search projects…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search projects"
          />
        </label>
        <div className="proj-chips" role="group" aria-label="Filter by technology">
          {tags.map((t) => (
            <button
              key={t.name}
              type="button"
              className={`proj-chip${tag === t.name ? " on" : ""}`}
              aria-pressed={tag === t.name}
              onClick={() => setTag(t.name)}
            >
              {t.name} <em>{t.count}</em>
            </button>
          ))}
        </div>
        <button
          type="button"
          className="proj-sort"
          onClick={() => setSort((s) => (s === "newest" ? "oldest" : "newest"))}
        >
          {sort === "newest" ? "Newest first ↓" : "Oldest first ↑"}
        </button>
      </div>

      <p className="proj-count" aria-live="polite">
        Showing {shown.length} of {all.length} projects
        {tag !== "All" && <> · <strong>{tag}</strong></>}
      </p>

      {shown.length === 0 ? (
        <div className="proj-empty">
          <p>No projects match “{query || tag}”.</p>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setTag("All");
              setQuery("");
            }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="proj-grid">
          {shown.map((p, i) => (
            <article className="proj-card" key={p.name}>
              <div className="proj-top">
                <span className="proj-num">{String(i + 1).padStart(2, "0")}</span>
                <span className="chip chip-mono">{fmt(p.createdAt)}</span>
              </div>
              <h3 className="proj-name">{p.name}</h3>
              <p className="proj-desc">{p.description}</p>
              <div className="proj-tags">
                {uniqueLangs(p).map((l) => (
                  <button
                    type="button"
                    key={l.name}
                    className={`proj-tag${tag === l.name ? " on" : ""}`}
                    onClick={() => setTag(tag === l.name ? "All" : l.name)}
                    title={`Filter by ${l.name}`}
                  >
                    <span className="iconify" data-icon={l.iconifyClass} data-inline="false" />
                    {l.name}
                  </button>
                ))}
              </div>
              {p.url && (
                <a className="proj-link" href={p.url} target="_blank" rel="noopener noreferrer">
                  View project <span aria-hidden="true">↗</span>
                </a>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
